import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Rating,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import { ArrowBack as BackIcon, Send as SendIcon } from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { useParams, useNavigate } from 'react-router-dom';
import { peerReviewService, PeerReviewAssignment } from '../../services/peerReviewService';

const PeerReviewForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState<PeerReviewAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (id) {
      loadAssignment();
    }
  }, [id]);

  const loadAssignment = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await peerReviewService.getAssignment(parseInt(id!));
      setAssignment(data);
      // Mark as in review
      if (data.status === 'pending') {
        await peerReviewService.startReview(parseInt(id!));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!rating) {
      setError(t('peerReview.errors.ratingRequired', 'Bitte geben Sie eine Bewertung ab'));
      return;
    }

    if (!feedback.trim()) {
      setError(t('peerReview.errors.feedbackRequired', 'Bitte geben Sie Feedback ein'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await peerReviewService.submitReview(parseInt(id!), {
        rating,
        feedback: feedback.trim(),
      });
      navigate('/peer-review', { state: { success: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error submitting review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!assignment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('peerReview.notFound', 'Auftrag nicht gefunden')}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/peer-review')} sx={{ mt: 2 }}>
          {t('common.back', 'Zurück')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/peer-review')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5">
          {t('peerReview.reviewTitle', 'Peer-Review: %{lesson}', { lesson: assignment.lessonTitle })}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Submission to review */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('peerReview.submissionToReview', 'Zu bewertende Abgabe')}
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
          }}
        >
          {assignment.submission}
        </Box>
      </Paper>

      {/* Review criteria if available */}
      {assignment.peerReviewCriteria && assignment.peerReviewCriteria.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('peerReview.criteria', 'Bewertungskriterien')}
          </Typography>
          {assignment.peerReviewCriteria.map((criterion, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">{criterion.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {criterion.description}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Review form */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('peerReview.yourReview', 'Ihre Bewertung')}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Typography component="legend" gutterBottom>
              {t('peerReview.ratingLabel', 'Gesamtbewertung')}
            </Typography>
            <Rating
              value={rating}
              onChange={(_, newValue) => setRating(newValue)}
              size="large"
            />
          </Box>

          <TextField
            fullWidth
            multiline
            rows={6}
            label={t('peerReview.feedbackLabel', 'Ihr Feedback')}
            placeholder={t('peerReview.feedbackPlaceholder', 'Geben Sie konstruktives Feedback zur Abgabe. Was war gut? Was könnte verbessert werden?')}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={submitting}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => navigate('/peer-review')} disabled={submitting}>
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              disabled={submitting || !rating || !feedback.trim()}
            >
              {t('peerReview.submitReview', 'Bewertung absenden')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default PeerReviewForm;
